import { useInfiniteQuery, useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import type { ClipPlayback, ClipPublic, DailyClip, EmbeddedClipTag } from '@proref/types';
import type { TagClipInput } from '@proref/schemas';
import {
  createUploadUrl,
  deleteClip,
  getClip,
  listClips,
  pinClip,
  publishClip,
  recordView,
  tagClip,
  unpinClip,
  unpublishClip,
  uploadToS3,
  markUploadComplete,
} from '../services/clips.service';

// ─── Cache invalidation ─────────────────────────────────────────────────────────
// EVERY cache entry that can hold a clip. Any clip mutation — tag, publish, unpublish, delete,
// upload — can change any of them, so they are invalidated together rather than per-hook.
//
// Listing keys hook-by-hook is exactly what caused the bug this replaces: every mutation dutifully
// invalidated the admin list and nothing else, so a clip deleted from the admin page kept showing
// in the referee library. The library's 5-minute staleTime meant even navigating back to it didn't
// refetch — the deletion was invisible until a hard reload.
//
// Prefix matching carries most of the weight: React Query matches a key prefix, so ['referee-clips']
// covers the plain library and its infinite-scroll pages in one entry, and ['clip'] covers every
// single-clip read. Add a new clip query? Its prefix belongs here.
const CLIP_QUERY_PREFIXES: string[][] = [
  ['clips'], // admin clips table + the super_admin cross-tenant console slice
  ['referee-clips'], // referee library: plain + infinite-scroll pages
  ['daily-clip'], // Home hero AND the library's own player (the M9 daily assignment, one shared key)
  ['clip'], // single-clip reads: the editor, the player, the status poll
];

// Invalidate every clip-derived cache. Active queries refetch immediately (invalidateQueries
// ignores staleTime for those), inactive ones refetch on their next mount — so the referee library
// is correct whether it's on screen when the admin deletes or visited afterwards.
export function invalidateClipCaches(qc: QueryClient): void {
  for (const queryKey of CLIP_QUERY_PREFIXES) {
    void qc.invalidateQueries({ queryKey });
  }
}

// Visible clips for the actor. Live-polls while any clip is still moving through the
// pipeline (uploading/processing) so the admin sees status transitions without refreshing;
// when every clip is settled (ready or failed) polling stops to avoid background noise.
export function useClipsQuery() {
  return useQuery({
    queryKey: ['clips'],
    // Short stale window — this list is edited often (upload/tag/publish) and the operator
    // expects near-live state. Independent of refetchInterval below (which polls in-flight
    // uploads regardless of staleness); the two must not be conflated.
    staleTime: 15_000,
    queryFn: () => listClips(),
    refetchInterval: (query) => {
      const data = (query.state.data as ClipPublic[] | undefined) ?? [];
      const inFlight = data.some((c) => c.status === 'uploading' || c.status === 'processing');
      // Return `false` (not 0) to fully disable polling — `0` would refetch as fast as
      // possible and pin the connection.
      return inFlight ? 5000 : false;
    },
    // Keep polling while the tab is backgrounded. Transcoding is exactly the thing an admin
    // starts and then switches away from, and by default React Query pauses interval refetches
    // for an unfocused window — so the list they came back to still showed the stale stage.
    refetchIntervalInBackground: true,
  });
}

// Super_admin clips console — the cross-tenant library sliced by a chosen sport (and optionally
// a federation). Distinct cache key from useClipsQuery so the federation_admin table (which polls
// in-flight uploads) and the super_admin console don't clobber each other. Disabled until a sport
// is picked (the landing shows sport cards, not a list). Polls while any clip is still
// transcoding so a federation's fresh upload settles on screen without a manual refresh.
export function useAdminClipsQuery(params: { sportId?: string; federationId?: string }) {
  const { sportId, federationId } = params;
  return useQuery({
    queryKey: ['clips', { sportId: sportId ?? null, federationId: federationId ?? null }],
    queryFn: () => listClips({ sportId, federationId }),
    enabled: Boolean(sportId),
    staleTime: 15_000,
    refetchInterval: (query) => {
      const data = (query.state.data as ClipPublic[] | undefined) ?? [];
      const inFlight = data.some((c) => c.status === 'uploading' || c.status === 'processing');
      return inFlight ? 5000 : false;
    },
  });
}

// Referee-facing library list — optionally narrowed by rule article. Distinct key from
// useClipsQuery so the admin Clips page (which polls in-flight statuses) and the referee
// library don't share a cache entry; the referee result is filtered server-side per the
// query param and shouldn't fall back to the admin's broader cached payload.
export function useRefereeClipsQuery(ruleArticleId: string | undefined) {
  return useQuery({
    queryKey: ['referee-clips', { ruleArticleId: ruleArticleId ?? null }],
    // published: true — the library is the referee's shelf, so it shows only live clips for every
    // role. Referees are pinned to published server-side anyway; this is what stops an ADMIN's own
    // drafts appearing here (they belong on the admin Clips page, which shows their real status).
    queryFn: () => listClips({ ruleArticleId, published: true }),
    // New clips only trickle into the library — several minutes of staleness is imperceptible, and a
    // long window means a referee flipping between rules/pages serves from cache with no spinner.
    staleTime: 5 * 60_000,
    // Show the previous filter's rows while the new key loads, instead of dropping to a skeleton.
    // This does NOT make data staler: staleTime and invalidation are untouched, so a mutation
    // elsewhere still refetches and replaces this immediately. It only fills the gap where a key
    // change has no cached entry yet — and `isPlaceholderData` lets the page mark it provisional.
    placeholderData: keepPreviousData,
  });
}

// How many library cards each infinite-scroll page pulls. A page short of this signals the end
// of the list (no server-side total needed) — see getNextPageParam below.
export const REFEREE_LIBRARY_PAGE_SIZE = 10;

// The referee library, paged for infinite scroll. Separate from useRefereeClipsQuery (which the
// Home count + Recommended strip still use to pull the whole set) so those unbounded reads and the
// windowed library don't share a cache entry. The next page's offset is simply pages-loaded ×
// page-size; a page returning fewer than a full window means we've reached the end.
export function useRefereeClipsInfiniteQuery(filters: { ruleArticleId?: string; tagId?: string } = {}) {
  const { ruleArticleId, tagId } = filters;
  return useInfiniteQuery({
    queryKey: ['referee-clips', 'infinite', { ruleArticleId: ruleArticleId ?? null, tagId: tagId ?? null }],
    queryFn: ({ pageParam }) =>
      listClips({ ruleArticleId, tagId, published: true, limit: REFEREE_LIBRARY_PAGE_SIZE, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.length < REFEREE_LIBRARY_PAGE_SIZE ? undefined : allPages.length * REFEREE_LIBRARY_PAGE_SIZE,
    // Match useRefereeClipsQuery: a long stale window keeps scrolling back to the library instant
    // (cached pages repaint, no spinner) while any new clips reconcile in the background.
    staleTime: 5 * 60_000,
    // Show the previous filter's rows while the new key loads, instead of dropping to a skeleton.
    // This does NOT make data staler: staleTime and invalidation are untouched, so a mutation
    // elsewhere still refetches and replaces this immediately. It only fills the gap where a key
    // change has no cached entry yet — and `isPlaceholderData` lets the page mark it provisional.
    placeholderData: keepPreviousData,
  });
}

// Single clip — used by the player flow once it exists; included here so all clip queries
// share a service surface. The signed playback URL refreshes on every fetch (1h TTL).
export function useClipQuery(id: string | undefined) {
  return useQuery({
    queryKey: ['clip', id],
    queryFn: () => getClip(id as string),
    enabled: Boolean(id),
    // Carries a 1h signed playback URL — fine to reuse within the session, but must never be
    // persisted across reloads (moot today: cache is in-memory only, no IndexedDB persistence).
    staleTime: 30_000,
  });
}

// Poll a single clip's transcoding status while it's still in flight. The upload wizard uses this
// to jump straight into the editor the moment MediaConvert finishes. Shares the ['clip', id] cache
// key with useClipQuery, so the editor we navigate to on Ready opens against a warm cache instead
// of a cold fetch. Polling stops the instant the clip settles (ready/failed) — no idle traffic.
export function useClipStatusQuery(clipId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ['clip', clipId],
    queryFn: () => getClip(clipId as string),
    enabled: enabled && Boolean(clipId),
    // Don't serve a stale cached clip here — the whole point is to see the status transition.
    staleTime: 0,
    refetchInterval: (query) => {
      const status = (query.state.data as ClipPlayback | undefined)?.status;
      return status === 'uploading' || status === 'processing' ? 4000 : false;
    },
  });
}

// Log a playback view. Fire-and-forget from the player; on success we patch the cached clip's
// view count to the server's fresh value so the number ticks up for the viewer without a full
// refetch. No toast — it's ambient.
export function useRecordView() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (clipId: string) => recordView(clipId),
    onSuccess: (result, clipId) => {
      const patch = (old: ClipPlayback | undefined): ClipPlayback | undefined =>
        old && old.id === clipId ? { ...old, views: result.views } : old;
      // A picked library clip.
      qc.setQueryData<ClipPlayback>(['clip', clipId], patch);
      // The M9 daily-assignment endpoint (`['daily-clip']`) wraps the clip in `{ clip, answered }`
      // — a different shape/key the patch above never touches.
      qc.setQueryData<DailyClip>(['daily-clip'], (old) =>
        old?.clip?.id === clipId ? { ...old, clip: { ...old.clip, views: result.views } } : old,
      );
    },
  });
}

// Step 1 of the upload flow: create the draft row + get the presigned URL.
export function useCreateUploadUrl() {
  return useMutation({ mutationFn: createUploadUrl });
}

// Step 2 of the upload flow: PUT the file to S3 with the presigned URL.
// Split as a separate mutation so the page can render distinct progress states
// ("Preparing…" vs "Uploading…"). A failure here is rolled back by the caller via
// useDiscardDraftClip — see below for why the draft can't just be left behind.
export function useUploadToS3() {
  return useMutation({
    mutationFn: ({ url, file }: { url: string; file: File }) => uploadToS3(url, file),
  });
}

// Report that the presigned PUT finished, moving the clip from `uploading` to `processing`.
// Fire-and-forget from the caller's perspective — the bytes are safely in S3 and MediaConvert is
// already triggered by the S3 event, so a failure here only costs a stale-looking status chip and
// must never turn a successful upload into a failed one.
export function useMarkUploadComplete() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => markUploadComplete(id),
    onSuccess: () => {
      invalidateClipCaches(qc);
    },
  });
}

// Roll back the draft row when its upload never lands. The presign step (step 1) creates the clip
// row BEFORE any bytes are sent — the transcoding webhook needs something to resolve the UUID
// against — so a failed S3 PUT strands that row in `uploading` forever: it shows in the admin's
// clips list, is polled every 5s by useClipsQuery as "in flight", and can never transition because
// no object will ever land to trigger MediaConvert. Deleting it is safe: an `uploading` draft is
// never published, so the API's publish gate can't reject the delete.
//
// Separate from useDeleteClip because that one binds its id at hook-creation time, while the draft
// id only exists mid-flight.
export function useDiscardDraftClip() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteClip(id),
    // Refresh the list either way — on success the stranded row is gone, and on failure the list
    // should still show whatever the server actually has.
    onSettled: () => {
      invalidateClipCaches(qc);
    },
  });
}

// Partial tag update. Invalidates both the single-clip query (so the editor immediately
// reflects the new values) and the list (so the row in the upload-clip list updates too).
export function useTagClip(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TagClipInput) => tagClip(id, input),
    onSuccess: () => {
      invalidateClipCaches(qc);
    },
  });
}

// Optimistically set a clip's managed tags. The editor's Tags section toggles chips constantly, so
// waiting for the round-trip + refetch felt laggy — this patches the cached clip's `tags` the instant
// the admin clicks (the chips read straight off it), rolls back on error, and reconciles on settle.
// Takes the full desired tag objects (not just ids) so the optimistic patch has labels/colours to
// render; the API call only needs their ids. No success toast — the instant chip flip IS the
// confirmation; only a failure (rollback) is worth surfacing.
export function useSetClipTags(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (nextTags: EmbeddedClipTag[]) => tagClip(id, { tag_ids: nextTags.map((t) => t.id) }),
    onMutate: async (nextTags) => {
      // Stop any in-flight clip refetch from clobbering the optimistic write mid-flight.
      await qc.cancelQueries({ queryKey: ['clip', id] });
      const previous = qc.getQueryData<ClipPlayback>(['clip', id]);
      if (previous) {
        const tags = [...nextTags].sort((a, b) => a.label.localeCompare(b.label));
        qc.setQueryData<ClipPlayback>(['clip', id], { ...previous, tags });
      }
      // Hand the pre-toggle snapshot to onError for rollback.
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(['clip', id], ctx.previous);
    },
    onSettled: () => {
      // Reconcile against the server's authoritative tags + refresh the list row chips.
      invalidateClipCaches(qc);
    },
  });
}

// Publish a draft. Same invalidation set as the tag mutation so the badge in the list
// flips to "Published" without a refresh.
export function usePublishClip(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => publishClip(id),
    onSuccess: () => {
      invalidateClipCaches(qc);
    },
  });
}

// Pull a live clip back to draft. Same invalidation set so the list chip flips from
// Published → Draft and the editor's Publish panel re-renders without a manual refresh.
export function useUnpublishClip(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => unpublishClip(id),
    onSuccess: () => {
      invalidateClipCaches(qc);
    },
  });
}

// Pin this clip as the federation's daily clip for today. Same invalidation set as publish/
// unpublish — the editor's toggle and the list row both need to reflect the new pin state.
export function usePinClip(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => pinClip(id),
    onSuccess: () => {
      invalidateClipCaches(qc);
    },
  });
}

// Undo the above.
export function useUnpinClip(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => unpinClip(id),
    onSuccess: () => {
      invalidateClipCaches(qc);
    },
  });
}

// Hard delete the clip + its S3 prefix. After success we invalidate the LIST query (so
// the row disappears) AND remove the single-clip cache entry (so a stale tab on the
// editor route doesn't keep rendering the dead clip from the cache while it 404s).
export function useDeleteClip(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => deleteClip(id),
    onSuccess: () => {
      // Drop the deleted clip's own entry outright — invalidating would refetch a 404.
      qc.removeQueries({ queryKey: ['clip', id] });
      invalidateClipCaches(qc);
    },
  });
}
